import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, extractError } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ImageUpload from "@/components/ImageUpload";
import { AlertTriangle, Trash2, ShieldCheck, Building2, Phone, Share2, CreditCard, Ban, Bell, Globe, Palette, Save } from "lucide-react";

const ALL_LANGS = [
  { code: "fr", label: "Français" },
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "pt", label: "Português" },
  { code: "ar", label: "العربية" },
];
const ALL_CURRENCIES = ["XAF", "XOF", "EUR", "USD", "MAD", "KES", "NGN", "GHS", "GBP"];

export default function AdminSettings() {
  const { settings, refresh } = useSettings();
  const { user } = useAuth();
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [acc, setAcc] = useState({ email: "", full_name: "", phone: "", current_password: "", new_password: "" });
  const [savingAcc, setSavingAcc] = useState(false);

  useEffect(() => { setForm(settings); }, [settings]);
  useEffect(() => {
    if (user) setAcc((a) => ({ ...a, email: user.email, full_name: user.full_name || "", phone: user.phone || "" }));
  }, [user]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleInArr = (key, val) => {
    const arr = Array.isArray(form[key]) ? form[key] : [];
    const next = arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val];
    upd(key, next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        commission_percent: parseFloat(form.commission_percent) || 0,
        min_deposit_amount: parseFloat(form.min_deposit_amount) || 0,
        cancel_full_refund_hours: parseInt(form.cancel_full_refund_hours) || 0,
        cancel_partial_refund_hours: parseInt(form.cancel_partial_refund_hours) || 0,
        cancel_partial_refund_pct: parseFloat(form.cancel_partial_refund_pct) || 0,
      };
      await api.put("/settings", payload);
      toast.success("Paramètres enregistrés");
      refresh();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };

  const resetAll = async () => {
    const confirm1 = window.prompt("Cette action supprimera DÉFINITIVEMENT toutes les agences, véhicules, réservations et utilisateurs (sauf votre compte admin).\n\nPour confirmer, tapez : SUPPRIMER");
    if (confirm1 !== "SUPPRIMER") { toast.info("Réinitialisation annulée"); return; }
    setResetting(true);
    try {
      const { data } = await api.post("/admin/reset-data");
      toast.success(`Base réinitialisée : ${data.agencies_deleted} agences, ${data.vehicles_deleted} véhicules, ${data.bookings_deleted} réservations supprimées`);
    } catch (e) { toast.error(extractError(e)); }
    finally { setResetting(false); }
  };

  const saveAccount = async () => {
    if (!acc.current_password) { toast.error("Veuillez saisir votre mot de passe actuel"); return; }
    setSavingAcc(true);
    try {
      const payload = { current_password: acc.current_password };
      if (acc.email && acc.email !== user.email) payload.email = acc.email;
      if (acc.full_name !== user.full_name) payload.full_name = acc.full_name;
      if (acc.phone !== user.phone) payload.phone = acc.phone;
      if (acc.new_password) payload.new_password = acc.new_password;
      await api.put("/admin/account", payload);
      toast.success("Compte mis à jour. Reconnexion requise si vous avez changé l'email ou le mot de passe.");
      setAcc((a) => ({ ...a, current_password: "", new_password: "" }));
    } catch (e) { toast.error(extractError(e)); }
    finally { setSavingAcc(false); }
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-8">
        <div>
          <h1 className="font-display text-3xl font-black text-[#0a1e3f]">Paramètres généraux</h1>
          <p className="text-sm text-gray-500 mt-1">Modifiez toutes les informations de votre plateforme sans toucher au code.</p>
        </div>
        <Button data-testid="save-settings" onClick={save} disabled={saving} className="mbk-btn-primary text-white h-12 px-6 rounded-full gap-2">
          <Save className="h-4 w-4" /> {saving ? "Enregistrement…" : "Enregistrer"}
        </Button>
      </div>

      <Tabs defaultValue="company" className="w-full">
        <TabsList className="mb-6 flex flex-wrap gap-1 bg-white p-1.5 rounded-2xl border border-gray-100 mbk-card-shadow h-auto">
          <TabsTrigger data-testid="tab-company" value="company" className="gap-1.5 rounded-xl"><Building2 className="h-4 w-4" /> Entreprise</TabsTrigger>
          <TabsTrigger data-testid="tab-brand" value="brand" className="gap-1.5 rounded-xl"><Palette className="h-4 w-4" /> Identité</TabsTrigger>
          <TabsTrigger data-testid="tab-contact" value="contact" className="gap-1.5 rounded-xl"><Phone className="h-4 w-4" /> Coordonnées</TabsTrigger>
          <TabsTrigger data-testid="tab-social" value="social" className="gap-1.5 rounded-xl"><Share2 className="h-4 w-4" /> Réseaux</TabsTrigger>
          <TabsTrigger data-testid="tab-payments" value="payments" className="gap-1.5 rounded-xl"><CreditCard className="h-4 w-4" /> Paiements</TabsTrigger>
          <TabsTrigger data-testid="tab-cancel" value="cancel" className="gap-1.5 rounded-xl"><Ban className="h-4 w-4" /> Annulation</TabsTrigger>
          <TabsTrigger data-testid="tab-notif" value="notif" className="gap-1.5 rounded-xl"><Bell className="h-4 w-4" /> Notifications</TabsTrigger>
          <TabsTrigger data-testid="tab-i18n" value="i18n" className="gap-1.5 rounded-xl"><Globe className="h-4 w-4" /> Langues & Devises</TabsTrigger>
          <TabsTrigger data-testid="tab-account" value="account" className="gap-1.5 rounded-xl"><ShieldCheck className="h-4 w-4" /> Compte</TabsTrigger>
        </TabsList>

        {/* --- Entreprise --- */}
        <TabsContent value="company">
          <Card title="Informations légales">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <F l="Raison sociale (dénomination légale)"><Input data-testid="s-company-legal" value={form.company_legal_name || ""} onChange={(e) => upd("company_legal_name", e.target.value)} /></F>
              <F l="Nom commercial"><Input data-testid="s-commercial" value={form.commercial_name || ""} onChange={(e) => upd("commercial_name", e.target.value)} /></F>
              <F l="Pays"><Input value={form.country || ""} onChange={(e) => upd("country", e.target.value)} placeholder="Congo" /></F>
              <F l="Devise par défaut">
                <Select value={form.default_currency || "XAF"} onValueChange={(v) => upd("default_currency", v)}>
                  <SelectTrigger data-testid="s-default-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>{ALL_CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F l="N° SIREN / SIRET (facultatif)"><Input value={form.siren || ""} onChange={(e) => upd("siren", e.target.value)} placeholder="À compléter plus tard" /></F>
              <F l="N° de TVA (facultatif)"><Input value={form.vat_number || ""} onChange={(e) => upd("vat_number", e.target.value)} /></F>
              <div className="md:col-span-2"><F l="Adresse du siège social"><Input value={form.head_office_address || ""} onChange={(e) => upd("head_office_address", e.target.value)} /></F></div>
            </div>
          </Card>
        </TabsContent>

        {/* --- Identité de marque --- */}
        <TabsContent value="brand">
          <Card title="Identité visuelle">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <F l="Nom du site (affiché)"><Input data-testid="s-name" value={form.site_name || ""} onChange={(e) => upd("site_name", e.target.value)} /></F>
              <F l="Slogan / tagline"><Input value={form.tagline || ""} onChange={(e) => upd("tagline", e.target.value)} /></F>
              <div><Label className="text-xs uppercase tracking-widest text-gray-500 font-bold">Logo</Label><div className="mt-2"><ImageUpload value={form.logo_url} onChange={(v) => upd("logo_url", v)} label="Logo" testId="logo" /></div></div>
              <div><Label className="text-xs uppercase tracking-widest text-gray-500 font-bold">Favicon (icône navigateur)</Label><div className="mt-2"><ImageUpload value={form.favicon_url} onChange={(v) => upd("favicon_url", v)} label="Favicon" testId="favicon" /></div></div>
              <div className="md:col-span-2"><Label className="text-xs uppercase tracking-widest text-gray-500 font-bold">Bannière d'accueil</Label><div className="mt-2"><ImageUpload value={form.banner_url} onChange={(v) => upd("banner_url", v)} label="Bannière" testId="banner" /></div></div>
              <F l="Couleur primaire"><Input type="color" value={form.primary_color || "#0a1e3f"} onChange={(e) => upd("primary_color", e.target.value)} className="h-11" /></F>
              <F l="Couleur secondaire"><Input type="color" value={form.secondary_color || "#d4a437"} onChange={(e) => upd("secondary_color", e.target.value)} className="h-11" /></F>
              <F l="Couleur accent"><Input type="color" value={form.accent_color || "#0e7a3f"} onChange={(e) => upd("accent_color", e.target.value)} className="h-11" /></F>
            </div>
          </Card>
        </TabsContent>

        {/* --- Coordonnées --- */}
        <TabsContent value="contact">
          <Card title="Coordonnées de la plateforme">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <F l="Email principal"><Input data-testid="s-email" value={form.contact_email || ""} onChange={(e) => upd("contact_email", e.target.value)} /></F>
              <F l="Email support"><Input data-testid="s-support-email" value={form.support_email || ""} onChange={(e) => upd("support_email", e.target.value)} /></F>
              <div className="md:col-span-2"><F l="Email admin (réception de toutes les notifications plateforme)"><Input data-testid="s-admin-notif-email" value={form.admin_notification_email || ""} onChange={(e) => upd("admin_notification_email", e.target.value)} placeholder="mobikarafrique@gmail.com" /></F></div>
              <div className="md:col-span-2">
                <button
                  type="button"
                  data-testid="btn-email-test"
                  onClick={async () => {
                    try {
                      const { data } = await api.post("/admin/email-test");
                      if (data.sent) toast.success(`Email test envoyé à ${data.to} via ${data.provider}`);
                      else if (data.resend_configured === false) toast.error("RESEND_API_KEY absente — email loggé en mock uniquement (Deploy → Secrets)");
                      else toast.error(`Échec Resend : ${data.reason || "voir logs"}`);
                    } catch (e) { toast.error(extractError(e)); }
                  }}
                  className="h-10 px-4 rounded-full bg-[#0e7a3f] hover:opacity-90 text-white text-sm font-semibold inline-flex items-center gap-2"
                >
                  📧 Envoyer un email test à l'admin
                </button>
                <p className="text-[11px] text-gray-500 mt-2">Utilise l'adresse ci-dessus. Vérifie aussi le dossier spam la première fois.</p>
              </div>
              <F l="Téléphone"><Input value={form.contact_phone || ""} onChange={(e) => upd("contact_phone", e.target.value)} /></F>
              <F l="WhatsApp (numéro complet)"><Input data-testid="s-whatsapp" value={form.whatsapp_number || ""} onChange={(e) => upd("whatsapp_number", e.target.value)} placeholder="+242…" /></F>
              <div className="md:col-span-2"><F l="Adresse (bureaux)"><Input value={form.address || ""} onChange={(e) => upd("address", e.target.value)} /></F></div>
              <div className="md:col-span-2"><F l="Adresse postale"><Input value={form.postal_address || ""} onChange={(e) => upd("postal_address", e.target.value)} /></F></div>
            </div>
          </Card>
        </TabsContent>

        {/* --- Réseaux sociaux --- */}
        <TabsContent value="social">
          <Card title="Réseaux sociaux">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <F l="Facebook"><Input value={form.social_facebook || ""} onChange={(e) => upd("social_facebook", e.target.value)} placeholder="https://facebook.com/…" /></F>
              <F l="Instagram"><Input value={form.social_instagram || ""} onChange={(e) => upd("social_instagram", e.target.value)} placeholder="https://instagram.com/…" /></F>
              <F l="TikTok"><Input data-testid="s-tiktok" value={form.social_tiktok || ""} onChange={(e) => upd("social_tiktok", e.target.value)} placeholder="https://tiktok.com/@…" /></F>
              <F l="LinkedIn"><Input value={form.social_linkedin || ""} onChange={(e) => upd("social_linkedin", e.target.value)} placeholder="https://linkedin.com/…" /></F>
              <F l="X (Twitter)"><Input value={form.social_twitter || ""} onChange={(e) => upd("social_twitter", e.target.value)} placeholder="https://x.com/…" /></F>
              <F l="YouTube"><Input data-testid="s-youtube" value={form.social_youtube || ""} onChange={(e) => upd("social_youtube", e.target.value)} placeholder="https://youtube.com/@…" /></F>
            </div>
          </Card>
        </TabsContent>

        {/* --- Paiements --- */}
        <TabsContent value="payments">
          <Card title="Paramètres de paiement">
            <div className="space-y-4">
              <Toggle testId="s-payments-enabled" label="Activer les paiements" description="Si désactivé, les clients ne pourront pas régler leur acompte en ligne." checked={!!form.payments_enabled} onChange={(v) => upd("payments_enabled", v)} />
              <Toggle testId="s-require-deposit" label="Confirmer la réservation uniquement après paiement de l'acompte" description="Tant que l'acompte n'est pas payé, le véhicule reste disponible à la réservation." checked={!!form.require_deposit_to_confirm} onChange={(v) => upd("require_deposit_to_confirm", v)} />
              <Toggle testId="s-auto-refund" label="Remboursements automatiques" description="Active le remboursement automatique lorsque les conditions d'annulation le permettent." checked={!!form.auto_refund_enabled} onChange={(v) => upd("auto_refund_enabled", v)} />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 pt-2">
                <F l="Commission MOBIKAR (%)"><Input data-testid="s-commission" type="number" step="0.5" min="0" max="100" value={form.commission_percent ?? 10} onChange={(e) => upd("commission_percent", e.target.value)} /></F>
                <F l="Acompte minimum (XAF)"><Input data-testid="s-min-deposit" type="number" min="0" value={form.min_deposit_amount ?? 0} onChange={(e) => upd("min_deposit_amount", e.target.value)} /></F>
                <F l="Mode de paiement">
                  <Select value={form.payment_mode || "deposit"} onValueChange={(v) => upd("payment_mode", v)}>
                    <SelectTrigger data-testid="s-payment-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="deposit">Acompte + solde à la remise</SelectItem>
                      <SelectItem value="full">Paiement intégral à la réservation</SelectItem>
                    </SelectContent>
                  </Select>
                </F>
              </div>
            </div>
          </Card>
          <div className="mt-6">
            <Card title="Paiement par transfert bancaire manuel">
              <p className="text-sm text-gray-600 mb-5">Alternative pour les clients sans carte bancaire. Le client déclare son virement, joint un justificatif, et vous validez manuellement depuis <b>Réservations</b>. Ces informations sont affichées au client lors du paiement.</p>
              <div className="space-y-4">
                <Toggle testId="s-transfer-enabled" label="Activer le paiement par transfert" description="Si désactivé, seul Stripe est proposé aux clients." checked={form.transfer_enabled !== false} onChange={(v) => upd("transfer_enabled", v)} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-2">
                  <F l="Bénéficiaire (Raison sociale exacte)"><Input data-testid="s-transfer-beneficiary" value={form.transfer_beneficiary || ""} onChange={(e) => upd("transfer_beneficiary", e.target.value)} placeholder="MOBIKAR SARL" /></F>
                  <F l="Nom de la banque"><Input data-testid="s-transfer-bank" value={form.transfer_bank_name || ""} onChange={(e) => upd("transfer_bank_name", e.target.value)} placeholder="Ecobank / BGFI / ..." /></F>
                  <F l="IBAN / N° de compte"><Input data-testid="s-transfer-iban" value={form.transfer_iban || ""} onChange={(e) => upd("transfer_iban", e.target.value)} placeholder="CG21 30011 00001 0000000000 00" /></F>
                  <F l="Code SWIFT / BIC"><Input data-testid="s-transfer-swift" value={form.transfer_swift || ""} onChange={(e) => upd("transfer_swift", e.target.value)} placeholder="ECOCCGCG" /></F>
                  <div className="md:col-span-2"><F l="Instructions complémentaires (optionnel)"><Textarea data-testid="s-transfer-instructions" rows={3} value={form.transfer_instructions || ""} onChange={(e) => upd("transfer_instructions", e.target.value)} placeholder="Ex : merci d'indiquer la référence de réservation en communication, envoyer le justificatif par mail à..." /></F></div>
                </div>
                <div className="p-4 bg-[#0e7a3f]/5 rounded-xl text-sm text-[#0a1e3f]">
                  <div className="font-bold mb-2">Aperçu client :</div>
                  <div className="text-gray-700 space-y-1">
                    <div><b>Bénéficiaire :</b> {form.transfer_beneficiary || <em className="text-gray-400">(à renseigner)</em>}</div>
                    <div><b>Banque :</b> {form.transfer_bank_name || <em className="text-gray-400">(à renseigner)</em>}</div>
                    <div><b>IBAN :</b> {form.transfer_iban || <em className="text-gray-400">(à renseigner)</em>}</div>
                    <div><b>SWIFT :</b> {form.transfer_swift || <em className="text-gray-400">(à renseigner)</em>}</div>
                    {form.transfer_instructions && <div className="text-xs text-gray-600 mt-2 italic">{form.transfer_instructions}</div>}
                  </div>
                </div>
              </div>
            </Card>
          </div>
          <div className="mt-6">
            <Card title="Paystack (Afrique)">
              <p className="text-sm text-gray-600 mb-5">Passerelle Afrique : cartes, Mobile Money, USSD, bank transfer. Devises supportées : XOF, NGN, GHS, KES, ZAR, USD. Les clés API se configurent dans les <b>Secrets Emergent</b> (PAYSTACK_SECRET_KEY, PAYSTACK_PUBLIC_KEY).</p>
              <div className="space-y-4">
                <Toggle testId="s-paystack-enabled" label="Activer Paystack" description="Si désactivé, l'option Paystack disparaît de la page de paiement." checked={form.paystack_enabled !== false} onChange={(v) => upd("paystack_enabled", v)} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-2">
                  <F l="Devise de facturation Paystack">
                    <Select value={form.paystack_currency || "XOF"} onValueChange={(v) => upd("paystack_currency", v)}>
                      <SelectTrigger data-testid="s-paystack-currency"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="XOF">XOF (Franc CFA Ouest — parité XAF)</SelectItem>
                        <SelectItem value="NGN">NGN (Naira nigérian)</SelectItem>
                        <SelectItem value="GHS">GHS (Cedi ghanéen)</SelectItem>
                        <SelectItem value="KES">KES (Shilling kényan)</SelectItem>
                        <SelectItem value="ZAR">ZAR (Rand sud-africain)</SelectItem>
                        <SelectItem value="USD">USD (Dollar US)</SelectItem>
                      </SelectContent>
                    </Select>
                  </F>
                  <Toggle testId="s-paystack-absorb" label="MOBIKAR absorbe les frais Paystack" description="Sinon les frais (~1.5%) sont répercutés au client." checked={form.paystack_absorb_fees !== false} onChange={(v) => upd("paystack_absorb_fees", v)} />
                </div>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
                  <b>ℹ️ Note pays</b> : Paystack couvre officiellement Nigeria, Ghana, Kenya, Afrique du Sud, Côte d'Ivoire, Rwanda, Égypte. Le Congo n'est pas encore support officiel — la conversion via XOF ou USD reste possible pour vos clients de ces pays.
                </div>
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* --- Politique d'annulation globale --- */}
        <TabsContent value="cancel">
          <Card title="Politique d'annulation globale">
            <p className="text-sm text-gray-600 mb-5">Cette politique s'applique par défaut à toutes les réservations. Chaque agence peut la surcharger.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <F l="Remboursement intégral si annulation plus de X heures avant la remise"><Input data-testid="s-full-hours" type="number" min="0" value={form.cancel_full_refund_hours ?? 48} onChange={(e) => upd("cancel_full_refund_hours", e.target.value)} /></F>
              <F l="Remboursement partiel entre X heures et le seuil ci-dessus"><Input data-testid="s-partial-hours" type="number" min="0" value={form.cancel_partial_refund_hours ?? 24} onChange={(e) => upd("cancel_partial_refund_hours", e.target.value)} /></F>
              <F l="Pourcentage remboursé sur créneau partiel (%)"><Input data-testid="s-partial-pct" type="number" min="0" max="100" value={form.cancel_partial_refund_pct ?? 50} onChange={(e) => upd("cancel_partial_refund_pct", e.target.value)} /></F>
              <div className="flex items-end">
                <Toggle testId="s-agency-full-refund" label="Si l'agence annule → remboursement intégral" description="Applique automatiquement le remboursement complet de l'acompte au client." checked={!!form.cancel_agency_full_refund} onChange={(v) => upd("cancel_agency_full_refund", v)} />
              </div>
              <div className="md:col-span-2"><F l="Notes complémentaires affichées au client"><Textarea rows={3} value={form.cancel_custom_text || ""} onChange={(e) => upd("cancel_custom_text", e.target.value)} placeholder="Texte additionnel expliquant votre politique…" /></F></div>
            </div>
            <div className="mt-6 p-4 bg-[#0e7a3f]/5 rounded-xl text-sm text-[#0a1e3f]">
              <div className="font-bold mb-2">Aperçu :</div>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>Plus de <b>{form.cancel_full_refund_hours ?? 48}h</b> avant la remise : remboursement intégral.</li>
                <li>Entre <b>{form.cancel_partial_refund_hours ?? 24}h</b> et {form.cancel_full_refund_hours ?? 48}h : {form.cancel_partial_refund_pct ?? 50} % remboursés.</li>
                <li>Moins de <b>{form.cancel_partial_refund_hours ?? 24}h</b> ou no-show : aucun remboursement.</li>
                {form.cancel_agency_full_refund && <li>Si l'agence annule : remboursement <b>intégral</b>.</li>}
              </ul>
            </div>
          </Card>
        </TabsContent>

        {/* --- Notifications --- */}
        <TabsContent value="notif">
          <Card title="Canaux de notification">
            <p className="text-sm text-gray-600 mb-5">Activez ou désactivez chaque canal. Ces bascules seront branchées automatiquement aux providers (Resend, Twilio, WhatsApp Business, Web Push) lorsque leurs clés API seront configurées.</p>
            <div className="space-y-4">
              <Toggle testId="s-notif-email" label="E-mails" description="Confirmations, rappels et reçus par email." checked={!!form.notif_email_enabled} onChange={(v) => upd("notif_email_enabled", v)} />
              <Toggle testId="s-notif-sms" label="SMS" description="Rappels critiques par SMS (Twilio)." checked={!!form.notif_sms_enabled} onChange={(v) => upd("notif_sms_enabled", v)} />
              <Toggle testId="s-notif-whatsapp" label="WhatsApp" description="Confirmations et messages via WhatsApp Business." checked={!!form.notif_whatsapp_enabled} onChange={(v) => upd("notif_whatsapp_enabled", v)} />
              <Toggle testId="s-notif-push" label="Notifications push" description="Notifications navigateur / mobile." checked={!!form.notif_push_enabled} onChange={(v) => upd("notif_push_enabled", v)} />
            </div>
          </Card>
        </TabsContent>

        {/* --- Langues & Devises --- */}
        <TabsContent value="i18n">
          <Card title="Langues activées">
            <p className="text-sm text-gray-600 mb-4">Cochez les langues à proposer dans le sélecteur du site.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {ALL_LANGS.map((l) => {
                const on = (form.enabled_languages || []).includes(l.code);
                return (
                  <button key={l.code} type="button" data-testid={`lang-${l.code}`} onClick={() => toggleInArr("enabled_languages", l.code)}
                    className={`text-sm font-semibold rounded-xl px-4 py-3 border transition text-left ${on ? "bg-[#0a1e3f] text-white border-[#0a1e3f]" : "bg-white text-gray-600 border-gray-200 hover:border-[#0a1e3f]"}`}>
                    <div className="font-bold">{l.label}</div>
                    <div className={`text-xs ${on ? "text-white/60" : "text-gray-400"}`}>{l.code.toUpperCase()}</div>
                  </button>
                );
              })}
            </div>
          </Card>
          <div className="mt-6">
            <Card title="Devises activées">
              <p className="text-sm text-gray-600 mb-4">Cochez les devises disponibles pour l'affichage des prix.</p>
              <div className="flex flex-wrap gap-2">
                {ALL_CURRENCIES.map((c) => {
                  const on = (form.enabled_currencies || []).includes(c);
                  return (
                    <button key={c} type="button" data-testid={`cur-${c}`} onClick={() => toggleInArr("enabled_currencies", c)}
                      className={`text-sm font-bold rounded-full px-4 py-2 border transition ${on ? "bg-[#0e7a3f] text-white border-[#0e7a3f]" : "bg-white text-gray-600 border-gray-200 hover:border-[#0e7a3f]"}`}>
                      {c}
                    </button>
                  );
                })}
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* --- Compte admin & zone danger --- */}
        <TabsContent value="account">
          <Card title="Mon compte administrateur" icon={<ShieldCheck className="h-5 w-5 text-[#0e7a3f]" />}>
            <p className="text-sm text-gray-600 mb-5">Modifiez votre nom, e-mail ou mot de passe. Votre mot de passe actuel est requis pour toute modification.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <F l="Nom complet"><Input data-testid="acc-name" value={acc.full_name} onChange={(e) => setAcc({ ...acc, full_name: e.target.value })} /></F>
              <F l="Téléphone"><Input data-testid="acc-phone" value={acc.phone} onChange={(e) => setAcc({ ...acc, phone: e.target.value })} /></F>
              <F l="Adresse e-mail de connexion"><Input data-testid="acc-email" value={acc.email} onChange={(e) => setAcc({ ...acc, email: e.target.value })} /></F>
              <F l="Nouveau mot de passe (facultatif)"><Input data-testid="acc-new-pw" type="password" placeholder="Laissez vide pour ne pas changer" value={acc.new_password} onChange={(e) => setAcc({ ...acc, new_password: e.target.value })} /></F>
              <div className="md:col-span-2 border-t border-gray-100 pt-4">
                <F l="Mot de passe actuel (obligatoire pour confirmer)"><Input data-testid="acc-current-pw" type="password" value={acc.current_password} onChange={(e) => setAcc({ ...acc, current_password: e.target.value })} /></F>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <Button data-testid="save-account" onClick={saveAccount} disabled={savingAcc} className="mbk-btn-green text-white h-11 px-6 rounded-full">
                {savingAcc ? "Enregistrement…" : "Mettre à jour mon compte"}
              </Button>
            </div>
          </Card>

          <div className="mt-8 border-2 border-red-200 rounded-2xl p-6 bg-red-50/40">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0"><AlertTriangle className="h-5 w-5 text-red-600" /></div>
              <div className="flex-1">
                <div className="font-display text-lg font-black text-red-700">Zone de danger</div>
                <p className="text-sm text-red-800/80 mt-1">Supprime toutes les agences, véhicules, réservations, utilisateurs non-admin et journaux. Votre compte admin et vos paramètres de marque sont préservés. Irréversible.</p>
                <Button data-testid="reset-data-btn" onClick={resetAll} disabled={resetting} variant="destructive" className="mt-4 h-11 rounded-full px-5">
                  <Trash2 className="h-4 w-4 mr-2" /> {resetting ? "Suppression…" : "Réinitialiser toutes les données"}
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const F = ({ l, children }) => (<div><Label className="text-xs uppercase tracking-widest text-gray-500 font-bold">{l}</Label><div className="mt-1.5">{children}</div></div>);
const Card = ({ title, icon, children }) => (
  <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-100 mbk-card-shadow">
    <div className="flex items-center gap-2 mb-5">
      {icon}
      <h2 className="font-display text-xl font-bold text-[#0a1e3f]">{title}</h2>
    </div>
    {children}
  </div>
);
const Toggle = ({ label, description, checked, onChange, testId }) => (
  <div className="flex items-start justify-between gap-4 p-4 rounded-xl border border-gray-100 bg-gray-50/50">
    <div className="flex-1">
      <div className="font-semibold text-[#0a1e3f] text-sm">{label}</div>
      {description && <div className="text-xs text-gray-500 mt-0.5">{description}</div>}
    </div>
    <Switch data-testid={testId} checked={checked} onCheckedChange={onChange} />
  </div>
);
